import * as React from 'react';
import { CompTableRow } from 'nomst/Components/CompReport/compTableRow';
import { Employee } from 'nomst/Stores/Decision/employee';
import { Configuration } from 'nomst/Stores/Configuration/configuration';
import { AmountCode } from 'typings/enums';
import { Amount } from 'nomst/Stores/Decision/amount';
import CompViewHelper from 'sharedLogic/compViewHelper';
import './compTable.scss';

export interface ICompTableProps {
    employee: Employee;
    configuration: Configuration;
}

const CompTable = (props: ICompTableProps): React.ReactElement => {
    const { employee, configuration } = props;

    const renderPayRange = () => {
        if (!configuration.payRangeConfiguration.showPayRange || !employee.basePayForPayRange || !employee.range) {
            return '';
        }

        const rangeComponent = <span data-mf-replace-inner="***" className="comp-table__pay-range">
            {CompViewHelper.getPayRangeValue(employee, configuration.payRangeConfiguration, configuration.format)}
        </span>;

        return (
            <span>
                {CompViewHelper.getPayRangeText(configuration.payRangeConfiguration.displayType)} {rangeComponent}
            </span>
        );
    };

    const renderAmount = (amount: Amount) => {
        const { title } = configuration.getCycleBudgetField(amount.amountCode);
        const percent = CompViewHelper.getAmountPercentage(amount, employee);
        const amountValue = !!percent && percent > 0 ? amount.value : undefined;

        return (
            <CompTableRow
                key={amount.amountCode}
                label={title}
                percent={percent}
                amount={amountValue}
                isHourly={employee.isHourly && amount.updateNewPay}
                currencyCode={employee.isoCurrencyCode}
                format={configuration.format}
            />
        );
    };

    const renderTccRow = (
        componentTitle: string,
        componentValue: number,
        doesComponentAffectTcc: boolean) => {
        if (!doesComponentAffectTcc) {
            return null;
        }

        return (
            <CompTableRow
                key={componentTitle}
                className="comp-table-row--non-cycle-amount"
                label={componentTitle}
                amount={componentValue}
                currencyCode={employee.isoCurrencyCode}
                format={configuration.format}
            />
        );
    };

    const renderTccAmounts = (doesAffectTcc: boolean) => {
        if (!configuration.teamPageViews.tcc) {
            return null;
        }

        const tccComponents: React.ReactNode[] = employee.amounts
            .filter(amount => CompViewHelper.shouldTccIncludeAmount(amount, configuration, doesAffectTcc))
            .map(renderAmount);

        const nonCycleAmounts = CompViewHelper.getNonCycleAmounts(employee, configuration, doesAffectTcc)
            .map(nonCycleAmount => renderTccRow(
                nonCycleAmount.title,
                nonCycleAmount.amount,
                nonCycleAmount.shouldRender));
        tccComponents.push(...nonCycleAmounts);
        return tccComponents;
    };

    const renderStock = () => {
        const { stockAmount } = employee;
        if (!stockAmount || !configuration.teamPageViews.isAmountUsedInCompSummary(AmountCode.Stock)) {
            return null;
        }

        return (
            <CompTableRow
                className="comp-table-row--stock"
                label="Stock"
                amount={CompViewHelper.getStockText(stockAmount, configuration.format)}
            />
        );
    };

    return (
        <div className="comp-table">
            <h3 className="comp-table__title">Compensation</h3>
            <CompTableRow
                className="comp-table-row--header"
                label={renderPayRange()}
                percent="% Awarded"
                amount="Amount"
            />
            {employee.amounts
                .filter(amount => amount.updateNewPay && CompViewHelper.shouldRenderAmount(amount, configuration))
                .map(renderAmount)}
            <CompTableRow
                className="comp-table-row--summary"
                label="New Pay"
                amount={employee.isHourly ? employee.newPay : employee.annualizedNewPay}
                isHourly={employee.isHourly}
                currencyCode={employee.isoCurrencyCode}
                format={configuration.format}
            />
            {renderTccAmounts(true)}
            {configuration.teamPageViews.tcc && <CompTableRow
                className="comp-table-row--summary"
                label="Total Cash Compensation"
                amount={employee.convertedTotalCombinedCompensation}
                currencyCode={employee.isoCurrencyCode}
                format={configuration.format}
            />}
            {renderTccAmounts(false)}
            {renderStock()}
        </div>
    );
};

export default CompTable;
export { CompTable };